import { type ReactElement, type ReactNode } from 'react';
import { PageLoadingFallback } from '@/features/metrics/components/PageLoadingFallback';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { TestingUploadResultsAction } from '@/features/shared/components/TestingUploadResultsAction';
import { EntityTrendsPageContentSection } from './EntityTrendsPageContentSection';
import { EntityTrendsUploadDialog } from './EntityTrendsUploadDialog';
import type { EntityTrendsPageViewModel } from '@/features/shared/hooks/useEntityTrendsPage';

type EntityTrendsPageShellBaseProps<TEntityItem> = {
  viewModel: EntityTrendsPageViewModel<TEntityItem>;
  title: string;
  addButtonLabel: string;
  searchAriaLabel: string;
  chartTabsAriaLabel: string;
  pageTabsAriaLabel: string;
  entityTabLabel: string;
  chartEmptyStateTitle: string;
  entityTableNoRowsText: string;
  executionTableNoRowsText: string;
  contentAbove?: ReactNode;
  contentBelow?: ReactNode;
  hideHeader?: boolean;
  hideTitleActions?: boolean;
};

type EntityTrendsPageShellOverrideProps = {
  contentOverride: ReactNode;
  renderEntityTable?: never;
  renderExecutionsTable?: never;
};

type EntityTrendsPageShellDefaultProps = {
  contentOverride?: undefined;
  renderEntityTable: (input: { showDeltas: boolean; noRowsText: string }) => ReactElement;
  renderExecutionsTable: (input: { noRowsText: string }) => ReactElement;
};

type EntityTrendsPageShellProps<TEntityItem> = EntityTrendsPageShellBaseProps<TEntityItem> &
  (EntityTrendsPageShellOverrideProps | EntityTrendsPageShellDefaultProps);

const unusedOverrideTableRenderer = (): ReactElement => <></>;

export const EntityTrendsPageShell = <TEntityItem,>({
  viewModel,
  title,
  addButtonLabel,
  searchAriaLabel,
  chartTabsAriaLabel,
  pageTabsAriaLabel,
  entityTabLabel,
  chartEmptyStateTitle,
  entityTableNoRowsText,
  executionTableNoRowsText,
  renderEntityTable,
  renderExecutionsTable,
  contentAbove,
  contentBelow,
  contentOverride,
  hideHeader = false,
  hideTitleActions = false,
}: EntityTrendsPageShellProps<TEntityItem>): ReactElement => {
  const titleActions = (
    <TestingUploadResultsAction onClick={viewModel.uploadFlow.openUploadDialog} />
  );
  const uploadDialogNode = (
    <EntityTrendsUploadDialog
      selectedProjectId={viewModel.selectedProjectId}
      selectedOrganisationId={viewModel.selectedOrganisationId}
      projectOptions={viewModel.projectOptions}
      uploadProjectIdFieldId={viewModel.uploadProjectIdFieldId}
      uploadFlow={viewModel.uploadFlow}
    />
  );
  const resolvedChartEmptyStateTitle = formatRangeWindowNoDataText(
    chartEmptyStateTitle,
    viewModel.range,
    viewModel.hasCustomDates,
  );
  const resolvedEntityTableNoRowsText = formatRangeWindowNoDataText(
    entityTableNoRowsText,
    viewModel.range,
    viewModel.hasCustomDates,
  );
  const resolvedExecutionTableNoRowsText = formatRangeWindowNoDataText(
    executionTableNoRowsText,
    viewModel.range,
    viewModel.hasCustomDates,
  );

  if (viewModel.showPageSkeleton) {
    return <PageLoadingFallback loadingMessageKey={viewModel.loadingMessageKey} />;
  }

  return (
    <EntityTrendsPageContentSection
      viewModel={viewModel}
      titleActions={hideTitleActions ? null : titleActions}
      footer={uploadDialogNode}
      title={title}
      addButtonLabel={addButtonLabel}
      searchAriaLabel={searchAriaLabel}
      chartTabsAriaLabel={chartTabsAriaLabel}
      pageTabsAriaLabel={pageTabsAriaLabel}
      entityTabLabel={entityTabLabel}
      chartEmptyStateTitle={resolvedChartEmptyStateTitle}
      entityTableNoRowsText={resolvedEntityTableNoRowsText}
      executionTableNoRowsText={resolvedExecutionTableNoRowsText}
      contentAbove={contentAbove}
      contentBelow={contentBelow}
      contentOverride={contentOverride}
      hideHeader={hideHeader}
      renderEntityTable={renderEntityTable ?? unusedOverrideTableRenderer}
      renderExecutionsTable={renderExecutionsTable ?? unusedOverrideTableRenderer}
    />
  );
};
